/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import type { DivisionItem } from '../../hooks/useSchedulerDivisions'
import type {
  SchedulerConstraint,
  CreateConstraintData,
} from '../../hooks/useSchedulerConstraints'
import type { Venue } from '../../hooks/useSchedulerLocations'
import type { TimeSlot } from '../../hooks/useSchedulerTimeSlots'
import type { GeneratedGame } from '../GeneratedGamesSection'
import DivisionDetailHeader from './DivisionDetailHeader'
import DivisionTeamsTable from './DivisionTeamsTable'
import DivisionConstraintsTable from './DivisionConstraintsTable'
import SchedulePreview from './SchedulePreview'

interface DivisionDetailProps {
  division: DivisionItem
  divisions?: DivisionItem[]
  constraints?: SchedulerConstraint[]
  games?: GeneratedGame[]
  timeSlots?: TimeSlot[]
  competition?: { starts_at?: string; ends_at?: string } | null
  onToggleReady: (divisionId: string) => void
  onToggleConstraintEnabled?: (constraintId: string, enabled: boolean) => void
  onUpdateConstraintType?: (constraintId: string, isHard: boolean) => void
  onDeleteTeam?: (divisionId: string, teamId: string, teamName: string) => void
  onAddConstraint?: (data: CreateConstraintData) => Promise<unknown>
  onDeleteConstraint?: (constraintId: string) => Promise<void>
  venues?: Venue[]
}

const DivisionDetail: React.FC<DivisionDetailProps> = ({
  division,
  divisions = [],
  constraints = [],
  games = [],
  timeSlots = [],
  competition = null,
  onToggleReady,
  onToggleConstraintEnabled,
  onUpdateConstraintType,
  onDeleteTeam,
  onAddConstraint,
  onDeleteConstraint,
  venues,
}) => {
  return (
    <div css={container}>
      <DivisionDetailHeader division={division} onToggleReady={onToggleReady} />
      <div>
        <DivisionTeamsTable
          division={division}
          constraints={constraints}
          onDeleteTeam={onDeleteTeam}
          onAddConstraint={onAddConstraint}
          onDeleteConstraint={onDeleteConstraint}
          onToggleConstraintEnabled={onToggleConstraintEnabled}
          onUpdateConstraintType={onUpdateConstraintType}
          divisionId={division.id}
          venues={venues}
        />
      </div>
      <DivisionConstraintsTable
        constraints={constraints}
        divisionId={division.id}
        onToggleEnabled={onToggleConstraintEnabled}
        onAddConstraint={onAddConstraint}
        onDeleteConstraint={onDeleteConstraint}
        onUpdateConstraintType={onUpdateConstraintType}
        venues={venues}
      />
      <SchedulePreview
        games={games}
        divisionId={division.id}
        venues={venues}
        divisions={divisions}
        timeSlots={timeSlots}
        constraints={constraints}
        competition={competition}
      />
    </div>
  )
}

const container = css`
  padding-bottom: 32px;
`

export default DivisionDetail
